/**
 * Helper per creare/validare giocatori, squadre, ordine turni.
 */

import type { GameConfig, Player, Team, TurnEntity } from './types';

let counter = 0;

function uid(prefix: string): string {
  counter += 1;
  return `${prefix}_${Date.now().toString(36)}_${counter}`;
}

export function createPlayer(name: string, teamId?: string): Player {
  return { id: uid('p'), name: name.trim(), teamId };
}

export function createTeam(name: string, color: string): Team {
  return { id: uid('t'), name: name.trim(), color };
}

export function defaultTurnOrder(config: Pick<GameConfig, 'mode' | 'players' | 'teams'>): TurnEntity[] {
  if (config.mode === 'teams') {
    return config.teams.map((t) => ({ kind: 'team', id: t.id }));
  }
  return config.players.map((p) => ({ kind: 'player', id: p.id }));
}

export function validateConfig(
  config: GameConfig,
  minPlayers: number,
  maxPlayers = Infinity,
): { ok: boolean; error?: string } {
  if (config.players.length < minPlayers) {
    return { ok: false, error: `min_players:${minPlayers}` };
  }
  if (config.players.some((p) => !p.name.trim())) {
    return { ok: false, error: 'empty_name' };
  }
  if (config.players.length > maxPlayers) return { ok: false, error: `max_players:${maxPlayers}` };
  if (config.mode === 'teams') {
    if (config.teams.length < 2) return { ok: false, error: 'min_teams' };
    if (config.teams.some((t) => !t.name.trim())) return { ok: false, error: 'empty_team_name' };
    if (config.players.some((p) => !config.teams.some((t) => t.id === p.teamId))) return { ok: false, error: 'unassigned_player' };
    if (config.teams.some((t) => !config.players.some((p) => p.teamId === t.id))) return { ok: false, error: 'empty_team' };
  }
  if (config.turnOrder.length === 0) return { ok: false, error: 'empty_order' };
  if (config.turnOrder.some((e) => e.kind === 'player' ? !config.players.some((p) => p.id === e.id) : !config.teams.some((t) => t.id === e.id))) return { ok: false, error: 'invalid_order' };
  return { ok: true };
}

/** Restituisce il nome leggibile di una TurnEntity. */
export function entityName(entity: TurnEntity, config: GameConfig): string {
  if (entity.kind === 'team') {
    return config.teams.find((t) => t.id === entity.id)?.name ?? '?';
  }
  return config.players.find((p) => p.id === entity.id)?.name ?? '?';
}

/**
 * Per le squadre: chi del team gioca questo turno?
 * Strategia: round-robin all'interno della squadra basato sul numero di guess già fatte.
 */
export function pickPlayerForTurn(
  entity: TurnEntity,
  config: GameConfig,
  pastTurnsByEntity: number,
): Player {
  if (entity.kind === 'player') {
    const p = config.players.find((pp) => pp.id === entity.id);
    if (!p) throw new Error(`Player ${entity.id} not found`);
    return p;
  }
  const members = config.players.filter((p) => p.teamId === entity.id);
  if (members.length === 0) throw new Error(`Team ${entity.id} has no players`);
  return members[pastTurnsByEntity % members.length];
}
